// src/components/dashboard/DualTimeline.js

import React, { useState, useEffect } from "react";
import { doc, updateDoc, getDoc, addDoc, collection, Timestamp, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
// ✅ NOUVEAU : Import du hook pour les notifications multi-canal
import { useNotificationTrigger } from "../../hooks/useNotificationTrigger";
// ✅ NOUVEAU : Imports pour la gestion des retards timeline
import NotificationScheduler from '../../services/NotificationScheduler';
import TimelineDelayService from '../../services/TimelineDelayService';

// ✅ MODIFICATION 1 : NOUVELLE FONCTION pour récupérer tous les utilisateurs par rôle
const getAllUsersByRoles = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('active', '==', true) // Seulement les utilisateurs actifs
    );
    
    const usersSnapshot = await getDocs(usersQuery);
    const usersByRole = {
      chargeAffaires: [],
      chefsProjets: [],
      superAdmins: []
    };
    
    usersSnapshot.docs.forEach(doc => {
      const userData = doc.data();
      const role = userData.role?.toLowerCase();
      
      switch (role) {
        case 'charge-affaire':
          usersByRole.chargeAffaires.push(doc.id);
          break;
        case 'projectmanager':
        case 'chef de projet':
          usersByRole.chefsProjets.push(doc.id);
          break;
        case 'superadmin':
        case 'super_admin':
        case 'admin':
          usersByRole.superAdmins.push(doc.id);
          break;
      }
    });
    
    console.log('📊 USERS-BY-ROLE: Utilisateurs récupérés:', usersByRole);
    return usersByRole;
    
  } catch (error) {
    console.error('❌ Erreur récupération utilisateurs par rôle:', error);
    return { chargeAffaires: [], chefsProjets: [], superAdmins: [] };
  }
};

const calculateProgress = (phases = []) => {
  if (phases.length === 0) return 0;
  const completed = phases.filter(
    (phase) => phase.status.toLowerCase() === "terminé"
  ).length;
  return Math.round((completed / phases.length) * 100);
};

const calculateGlobalProgress = (progressChargeAffaire, progressChefProjet) => {
  return Math.round((progressChargeAffaire + progressChefProjet) / 2);
};

export const defaultChargeAffairePhases = [
  { name: "Dossier signé", date: "", status: "Non réalisé" },
  { name: "Facture d'acompte N°1 transmise au client", date: "", status: "Non réalisé" },
  { name: "Acompte N°1 payé", date: "", status: "Non réalisé" },
  { name: "Réalisation de la déclaration préalable", date: "", status: "Non réalisé" },
  { name: "Transmission du pouvoir Enedis au client", date: "", status: "Non réalisé" },
  { name: "Autorisation DP reçue", date: "", status: "Non réalisé" },
  { name: "Pouvoir Enedis reçu", date: "", status: "Non réalisé" }
];

export const defaultChefProjetPhases = [
  { name: "Réception des informations du chargé d'affaire", date: "", status: "Non réalisé" },
  { name: "Organisation de la visite technique", date: "", status: "Non réalisé" },
  { name: "Relevés des passages de câbles", date: "", status: "Non réalisé" },
  { name: "Réalisation des plans de chaînes", date: "", status: "Non réalisé" },
  { name: "Réalisation des calculs et calepinages des structures", date: "", status: "Non réalisé" },
  { name: "Lister le matériel nécessaire", date: "", status: "Non réalisé" },
  { name: "Demande de raccordement Enedis", date: "", status: "Non réalisé" },
  { name: "Demande de devis à au moins 2 fournisseurs", date: "", status: "Non réalisé" },
  { name: "Réception et analyse des offres", date: "", status: "Non réalisé" },
  { name: "Transmission de la facture de situation N°2", date: "", status: "Non réalisé" },
  { name: "Commande du matériel avec délai de réception", date: "", status: "Non réalisé" },
  { name: "Communication du planning au client", date: "", status: "Non réalisé" },
  { name: "Organisation des moyens humains pour le chantier", date: "", status: "Non réalisé" },
  { name: "Création de la fiche de chantier", date: "", status: "Non réalisé" },
  { name: "Accusé réception des livraisons", date: "", status: "Non réalisé" },
  { name: "Transmission de la facture de situation N°3", date: "", status: "Non réalisé" },
  { name: "Date de début des travaux", date: "", status: "Non réalisé" },
  { name: "Organisation des visites de suivi et d'inspection", date: "", status: "Non réalisé" },
  { name: "Organisation de la mise en service de l'installation", date: "", status: "Non réalisé" },
  { name: "Réalisation du consuel", date: "", status: "Non réalisé" },
  { name: "Réalisation du PV de réception", date: "", status: "Non réalisé" },
  { name: "Transmission de la facture de solde", date: "", status: "Non réalisé" },
  { name: "Demande d'avis client", date: "", status: "Non réalisé" }
];

const DualTimeline = ({ dossierId, chargeAffairePhases, chefProjetPhases }) => {
  const { user, hasAnyRole } = useAuth();
  // ✅ NOUVEAU : Hook pour les notifications multi-canal
  const { triggerTimelineNotification } = useNotificationTrigger();
  
  const [chargePhases, setChargePhases] = useState([]);
  const [chefPhases, setChefPhases] = useState([]);
  const [dossierDetails, setDossierDetails] = useState(null);
  const [isCheckingDelays, setIsCheckingDelays] = useState(false);

  console.log("User dans DualTimeline:", user);

  // ✅ NOUVEAU : Fonction utilitaire pour vérifier manuellement les retards
  const handleCheckDelaysManually = async () => {
    if (!dossierId || !dossierDetails) {
      console.log("⚠️ DUAL-TIMELINE: Dossier ID ou détails manquants pour vérification manuelle");
      return;
    }

    setIsCheckingDelays(true);
    try {
      console.log('🔍 DUAL-TIMELINE: Vérification manuelle des retards...');
      
      await TimelineDelayService.checkDossierStepDelays({
        id: dossierId,
        clientId: dossierDetails.clientId || dossierDetails.userId,
        intervenants: [
          ...(dossierDetails.chargeAffaires || []),
          ...(dossierDetails.chefsProjets || []),
          ...(dossierDetails.superAdmins || [])
        ],
        createdAt: dossierDetails.createdAt || { toDate: () => new Date() },
        clientName: dossierDetails.clientName,
        assignedTo: dossierDetails.assignedTo,
        ...dossierDetails
      });
      
      console.log('✅ DUAL-TIMELINE: Vérification manuelle terminée');
      alert('Vérification des retards terminée. Consultez la console pour plus de détails.');
      
    } catch (error) {
      console.error('❌ DUAL-TIMELINE: Erreur vérification manuelle:', error);
      alert('Erreur lors de la vérification des retards');
    } finally {
      setIsCheckingDelays(false);
    }
  };

  // Charger les détails du dossier
  useEffect(() => {
    const fetchDossierDetails = async () => {
      try {
        const dossierRef = doc(db, "dossiers", dossierId);
        const dossierSnap = await getDoc(dossierRef);
        if (dossierSnap.exists()) {
          setDossierDetails(dossierSnap.data());
          console.log("Données du dossier chargées:", dossierSnap.data());
        } else {
          console.error("Dossier non trouvé:", dossierId);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du dossier :", err);
      }
    };

    fetchDossierDetails();
  }, [dossierId]);

  // Initialiser les phases de la timeline
  useEffect(() => {
    setChargePhases(chargeAffairePhases?.length ? chargeAffairePhases : defaultChargeAffairePhases);
    setChefPhases(chefProjetPhases?.length ? chefProjetPhases : defaultChefProjetPhases);
  }, [chargeAffairePhases, chefProjetPhases]);

  // Trouver la discussion existante pour ce dossier
  const getExistingDiscussion = async () => {
    try {
      console.log("Recherche de discussion pour le dossier:", dossierId);
      const discussionsQuery = query(
        collection(db, "discussions"),
        where("dossierId", "==", dossierId)
      );
      const discussionsSnap = await getDocs(discussionsQuery);
      
      if (!discussionsSnap.empty) {
        const discussion = { id: discussionsSnap.docs[0].id, ...discussionsSnap.docs[0].data() };
        console.log("Discussion existante trouvée:", discussion);
        return discussion;
      }
      console.log("Aucune discussion existante trouvée");
      return null;
    } catch (err) {
      console.error("Erreur lors de la recherche de discussion existante :", err);
      return null;
    }
  };

  // Trouver le client associé à ce dossier
  const getClientId = async () => {
    try {
      console.log("Recherche du client pour le dossier:", dossierId);
      const clientUsersQuery = query(
        collection(db, "clientUsers"),
        where("dossierId", "==", dossierId)
      );
      const clientSnap = await getDocs(clientUsersQuery);
      
      if (!clientSnap.empty) {
        const clientId = clientSnap.docs[0].id;
        console.log("Client trouvé:", clientId);
        return clientId;
      }
      console.log("Aucun client trouvé pour ce dossier");
      return null;
    } catch (err) {
      console.error("Erreur lors de la recherche du client :", err);
      return null;
    }
  };

  // Changer le statut d'une étape dans la timeline - VERSION FINALE CORRIGÉE
  const toggleStatus = async (timelineType, index) => {
    console.log("Tentative de modifier le statut:", { timelineType, index });
    console.log("User info:", user);
    
    const now = new Date().toISOString();
    const dossierRef = doc(db, "dossiers", dossierId);
    let updatedPhases;

    // Vérifier si l'utilisateur est connecté
    if (!user) {
      console.error("Utilisateur non connecté");
      alert("Vous devez être connecté pour modifier la timeline");
      return;
    }

    // Mise à jour du statut de l'étape
    if (timelineType === "charge") {
      updatedPhases = [...chargePhases];
      const currentStatus = updatedPhases[index].status.toLowerCase();
      updatedPhases[index] = {
        ...updatedPhases[index],
        status: currentStatus === "terminé" ? "Non réalisé" : "Terminé",
        date: currentStatus === "terminé" ? "" : now,
        // ✅ NOUVEAU : Ajouter les champs pour les retards
        completedAt: currentStatus === "terminé" ? null : Timestamp.now(),
        validatedAt: currentStatus === "terminé" ? null : Timestamp.now()
      };
      setChargePhases(updatedPhases);
    } else {
      updatedPhases = [...chefPhases];
      const currentStatus = updatedPhases[index].status.toLowerCase();
      updatedPhases[index] = {
        ...updatedPhases[index],
        status: currentStatus === "terminé" ? "Non réalisé" : "Terminé",
        date: currentStatus === "terminé" ? "" : now,
        // ✅ NOUVEAU : Ajouter les champs pour les retards
        completedAt: currentStatus === "terminé" ? null : Timestamp.now(),
        validatedAt: currentStatus === "terminé" ? null : Timestamp.now()
      };
      setChefPhases(updatedPhases);
    }

    console.log("Phases mises à jour:", updatedPhases);

    const progressCharge = calculateProgress(timelineType === "charge" ? updatedPhases : chargePhases);
    const progressChef = calculateProgress(timelineType === "chef" ? updatedPhases : chefPhases);
    const globalProgress = calculateGlobalProgress(progressCharge, progressChef);

    try {
      await updateDoc(dossierRef, {
        phasesChargeAffaire: timelineType === "charge" ? updatedPhases : chargePhases,
        phasesChefProjet: timelineType === "chef" ? updatedPhases : chefPhases,
        progressChargeAffaire: progressCharge,
        progressChefProjet: progressChef,
        progressGlobal: globalProgress
      });

      console.log("Mise à jour du dossier réussie");

      // ✅ MODIFICATION 2 : SYSTÈME UNIFIÉ DE NOTIFICATIONS - VERSION SÛRE AVEC TOUS LES RÔLES
      try {
        console.log("📧 NOTIFICATION-SYSTEM: Déclenchement du système unifié...");
        
        // Récupérer le client du dossier
        const clientId = await getClientId();
        const dossierRefName = dossierDetails?.clientName || dossierDetails?.titre || `Dossier ${dossierId.slice(0, 8)}`;
        
        // ✅ NOUVEAU : Récupérer TOUS les utilisateurs par rôle
        const allUsersByRole = await getAllUsersByRoles();
        
        // Récupérer tous les intervenants du dossier (SANS DOUBLONS)
        const dossierSnap = await getDoc(doc(db, "dossiers", dossierId));
        const notifiedUsers = new Set(); // Pour éviter les doublons
        
        if (dossierSnap.exists()) {
          const dossier = dossierSnap.data();
          
          // ✅ COMBINAISON SÛRE : Assignés dans le dossier + TOUS les utilisateurs des bons rôles
          const allIntervenants = [
            // Utilisateurs déjà assignés au dossier (PRÉSERVÉ)
            ...(dossier.chargeAffaires || []), 
            ...(dossier.chefsProjets || []), 
            ...(dossier.superAdmins || []),
            // ✅ NOUVEAU : TOUS les utilisateurs des bons rôles
            ...allUsersByRole.chargeAffaires,
            ...allUsersByRole.chefsProjets,
            ...allUsersByRole.superAdmins,
            // L'utilisateur connecté (au cas où)
            user.uid
          ];
          
          // Supprimer les doublons et les valeurs nulles/undefined
          const uniqueIntervenants = [...new Set(allIntervenants.filter(id => id))];
          
          console.log("📧 NOTIFICATION-SYSTEM: Intervenants combinés:", {
            dossierAssignés: {
              chargeAffaires: dossier.chargeAffaires || [],
              chefsProjets: dossier.chefsProjets || [],
              superAdmins: dossier.superAdmins || []
            },
            tousLesRôles: allUsersByRole,
            uniqueIntervenants: uniqueIntervenants
          });
          
          // 🔧 SOLUTION : UNE NOTIFICATION PAR UTILISATEUR - MULTI-CANAL UNIQUEMENT
          // Le NotificationDispatcher crée déjà la notification Firestore !
          for (const userId of uniqueIntervenants) {
            if (!notifiedUsers.has(userId)) {
              notifiedUsers.add(userId);
              
              const currentStatus = updatedPhases[index].status.toLowerCase();
              const stepName = updatedPhases[index].name;
              const actionText = currentStatus === "terminé" ? "terminée" : "annulée";
              const notificationMessage = `L'étape "${stepName}" a été ${actionText} - ${dossierRefName}`;
              
              // ✅ SEULEMENT les notifications multi-canal (qui incluent Firestore)
              await triggerTimelineNotification(
                userId,
                stepName,
                notificationMessage,
                dossierId,
                dossierRefName
              );
            }
          }
          
          console.log(`📧 NOTIFICATION-SYSTEM: ${notifiedUsers.size} utilisateurs uniques notifiés (assignés + tous rôles)`);
        }
        
        // Notification pour le client (séparée) - MULTI-CANAL UNIQUEMENT
        if (clientId && !notifiedUsers.has(clientId)) {
          const currentStatus = updatedPhases[index].status.toLowerCase();
          const stepName = updatedPhases[index].name;
          const actionText = currentStatus === "terminé" ? "terminée" : "annulée";
          const clientMessage = `L'étape "${stepName}" a été ${actionText}`;
          
          // ✅ SEULEMENT les notifications multi-canal (qui incluent Firestore)
          await triggerTimelineNotification(
            clientId,
            stepName,
            clientMessage,
            dossierId,
            dossierRefName
          );
          
          console.log("📧 NOTIFICATION-SYSTEM: Notification client envoyée");
        }
        
      } catch (notificationError) {
        console.error('❌ NOTIFICATION-SYSTEM: Erreur notifications:', notificationError);
        // Ne pas bloquer le processus principal si les notifications échouent
      }
      
      // ✅ NOUVEAU : Vérification des retards après validation d'une étape
      if (updatedPhases[index].status.toLowerCase() === "terminé") {
        console.log('🔄 DUAL-TIMELINE: Vérification des retards après validation étape...');
        
        // Attendre que Firestore soit mis à jour, puis vérifier les retards
        setTimeout(async () => {
          try {
            await NotificationScheduler.checkDossierAfterTimelineUpdate(dossierId);
            console.log('✅ DUAL-TIMELINE: Vérification des retards terminée');
          } catch (error) {
            console.error('❌ DUAL-TIMELINE: Erreur vérification retards:', error);
          }
        }, 2000); // Délai de 2 secondes pour s'assurer que Firestore est à jour
      }
      
      // 🔧 NOUVEAU : Déclencher les événements de rafraîchissement des compteurs
      console.log("🔄 TIMELINE: Déclenchement des événements de rafraîchissement...");
      
      // Délai court pour permettre à Firestore de se synchroniser
      setTimeout(() => {
        // Événement spécifique pour les notifications timeline
        window.dispatchEvent(new CustomEvent('timelineStepUpdated', { 
          detail: { 
            dossierId,
            stepName: updatedPhases[index].name,
            newStatus: updatedPhases[index].status.toLowerCase(),
            type: 'notification',
            timestamp: Date.now()
          }
        }));
        
        // Événement général pour forcer le rafraîchissement
        window.dispatchEvent(new CustomEvent('forceRefreshNotificationCounts', {
          detail: { 
            type: 'notification',
            source: 'timeline',
            timestamp: Date.now()
          }
        }));
        
        // Événement pour rafraîchir la timeline spécifiquement
        window.dispatchEvent(new CustomEvent('forceRefreshTimeline'));
        
        console.log("✅ TIMELINE: Événements de rafraîchissement envoyés");
      }, 1000);
      
    } catch (error) {
      console.error("Erreur lors de la mise à jour du dossier:", error);
      alert("Une erreur s'est produite lors de la mise à jour du dossier");
    }
  };

  return (
    <div className="p-8">
      <h2 className="text-3xl font-bold text-center mb-6">Timeline du Dossier</h2>
      
      {/* ✅ NOUVEAU : Outils administrateur pour vérification manuelle des retards */}
      {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'project_manager') && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h4 className="text-sm font-medium text-yellow-800 mb-2">
            🔍 Outils Administrateur - Gestion des Retards
          </h4>
          <button
            onClick={handleCheckDelaysManually}
            disabled={isCheckingDelays}
            className="inline-flex items-center px-3 py-1.5 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCheckingDelays ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-1.5"></div>
            ) : (
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {isCheckingDelays ? 'Vérification en cours...' : 'Vérifier les retards de ce dossier'}
          </button>
          <p className="text-xs text-yellow-700 mt-1">
            Vérification manuelle des délais d'étapes selon les règles configurées (7, 30, 15, 15, 15, 7 jours)
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-2xl font-semibold mb-4">Chargé(e) d'affaire</h3>
          {chargePhases.map((phase, index) => (
            <div key={index} className="mb-6 border-l-2 pl-4 relative">
              <div className={`absolute left-[-10px] top-1 w-4 h-4 ${phase.status.toLowerCase() === "terminé" ? "bg-green-600" : "bg-blue-600"} rounded-full`}></div>
              <div className="text-lg font-medium">{phase.name}</div>
              <div className="text-sm text-gray-500">{phase.date ? new Date(phase.date).toLocaleDateString() : "Date non définie"}</div>
              <div className="text-sm text-gray-500">Statut : {phase.status}</div>
              <button 
                onClick={() => toggleStatus("charge", index)} 
                className="mt-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-green-600 transition"
              >
                {phase.status.toLowerCase() === "terminé" ? "Annuler" : "Marquer comme réalisé"}
              </button>
            </div>
          ))}
        </div>
        <div>
          <h3 className="text-2xl font-semibold mb-4">Chef de Projet</h3>
          {chefPhases.map((phase, index) => (
            <div key={index} className="mb-6 border-l-2 pl-4 relative">
              <div className={`absolute left-[-10px] top-1 w-4 h-4 ${phase.status.toLowerCase() === "terminé" ? "bg-green-600" : "bg-blue-600"} rounded-full`}></div>
              <div className="text-lg font-medium">{phase.name}</div>
              <div className="text-sm text-gray-500">{phase.date ? new Date(phase.date).toLocaleDateString() : "Date non définie"}</div>
              <div className="text-sm text-gray-500">Statut : {phase.status}</div>
              <button 
                onClick={() => toggleStatus("chef", index)} 
                className="mt-2 bg-blue-600 text-white px-3 py-1 rounded hover:bg-green-600 transition"
              >
                {phase.status.toLowerCase() === "terminé" ? "Annuler" : "Marquer comme réalisé"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DualTimeline;